import React, { type MutableRefObject } from 'react';

import {
  Button,
  Divider,
  Radio,
  RadioGroup,
  Select,
  Text,
  Textarea,
  type TypeSelectOption,
  WarningIcon,
} from '@thefarmersfront/kitchen';

import { FormField } from '~/components/common';

import { useModal } from '~/hooks/common';
import { useGetFilterAllQuery } from '~/hooks/receive';

import { RECEIVE_TOOLTIP_MESSAGE } from '~/constants';

import { ReceivePickupAddressModal } from '../ReceivePickupAddressModal';

import type { TypePickupAddress, TypePickupAddressDataState, TypePickupData } from '~/types/receive.types';

import styles from './ReceivePickupInfo.module.scss';

const ADDRESS_TYPE_LABELS: Record<TypePickupAddressType, string> = {
  default: '기본 출고지',
  etc: '기타 출고지',
  etc1: '기타 출고지1',
  etc2: '기타 출고지2',
};

type TypePickupAddressType = 'default' | 'etc' | 'etc1' | 'etc2';

type TypeReceivePickupInfoProps = {
  // 읽기 전용 모드
  disabled?: boolean;
  /** 출고지 타입 */
  addressType: TypePickupAddressType;
  /** 커스텀 라벨 (기본값: ADDRESS_TYPE_LABELS[addressType]) */
  customLabel?: string;
  /** 픽업 정보 데이터 */
  pickupData?: TypePickupData;
  /** 픽업 정보 데이터 상태 */
  pickupDataState: TypePickupAddressDataState;
  /** 픽업 정보 데이터 변경 핸들러 */
  onPickupDataChange: (
    key: keyof Pick<TypePickupData, 'address' | 'isUseVehicleSize' | 'vehicleSize' | 'palletQuantity' | 'memo'>,
    data: TypePickupData
  ) => void;
  resetPickupDataState: (
    key: keyof Pick<TypePickupData, 'address' | 'isUseVehicleSize' | 'vehicleSize' | 'palletQuantity' | 'memo'>
  ) => void;
  /** 에러 필드 DOM 참조 객체 */
  errorFieldRefs: MutableRefObject<Record<string, HTMLElement | null>>;
  /**
   * 에러 키 prefix
   * @example '123.kurlyDefault'
   */
  errorKeyPrefix: string;
};

/**
 * [입고 관리 > 입고요청관리] 컬리/밀크런 픽업 정보 컴포넌트
 */
const ReceivePickupInfo = ({
  disabled = false,
  addressType,
  customLabel,
  pickupData = {
    zipCode: '',
    address: '',
    detailAddress: '',
    contactNumber: '',
    isUseVehicleSize: false,
    palletQuantity: '',
    memo: '',
  },
  pickupDataState,
  onPickupDataChange,
  resetPickupDataState,
  errorFieldRefs,
  errorKeyPrefix,
}: TypeReceivePickupInfoProps) => {
  const { data: filterOptions } = useGetFilterAllQuery('ADD_RECEIVING_GOODS');

  const hasPickupAddress = !!pickupData.address; // 주소가 있으면 등록된 것으로 간주

  const addressData: TypePickupAddress = {
    zipCode: pickupData.zipCode,
    address: pickupData.address,
    detailAddress: pickupData.detailAddress,
    contactNumber: pickupData.contactNumber,
  };

  const pickupAddressModal = useModal({
    onConfirm: (value?: TypePickupAddress) => {
      if (!value) return;

      const updatedData: TypePickupData = {
        ...pickupData,
        zipCode: value.zipCode,
        address: value.address,
        detailAddress: value.detailAddress,
        contactNumber: value.contactNumber,
      };
      onPickupDataChange('address', updatedData);
    },
  });

  const handleVehicleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'Y' | 'N';
    const newData = { ...pickupData, isUseVehicleSize: value === 'Y' };
    if (value === 'Y') {
      newData.vehicleSize = null;
    }
    onPickupDataChange('isUseVehicleSize', newData);
  };

  return (
    <>
      <Divider className={styles['divider']} color="gray100" />
      <div className={styles['container']}>
        <FormField
          ref={(el) => {
            if (el) errorFieldRefs.current[`${errorKeyPrefix}.address`] = el;
          }}
          label={`${customLabel || ADDRESS_TYPE_LABELS[addressType]} 정보`}
          required
          contents={
            hasPickupAddress ? (
              <>
                <Text className={styles['text']} size="large">
                  {pickupData.address}
                  {pickupData.detailAddress && ` ${pickupData.detailAddress}`} / {pickupData.contactNumber}
                </Text>
                {!disabled && (
                  <Button
                    className={styles['button']}
                    size="small"
                    color="tertiary"
                    onClick={pickupAddressModal.handleOpenModal}
                  >
                    수정
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  className={styles['button']}
                  size="small"
                  color="secondary"
                  disabled={disabled}
                  onClick={pickupAddressModal.handleOpenModal}
                >
                  등록
                </Button>
                <div className={styles['warning-message']}>
                  <WarningIcon size={16} color="#E22D2E" />
                  <Text size="large" color="error">
                    출고지 정보를 등록해주세요.
                  </Text>
                </div>
              </>
            )
          }
          contentsClassName={styles['inputs-wrapper']}
        />
        <FormField
          ref={(el) => {
            if (el) errorFieldRefs.current[`${errorKeyPrefix}.vehicleSize`] = el;
          }}
          label="진입 가능 차량"
          contents={
            <>
              <RadioGroup
                className={styles['radio-group']}
                name={`vehicleYn-${addressType}-${errorKeyPrefix}`}
                value={pickupData.isUseVehicleSize ? 'Y' : 'N'}
                size="medium"
                disabled={disabled}
                onChange={handleVehicleTypeChange}
              >
                <Radio value="N" label="선택안함" />
                <Radio value="Y" label="크기 지정" />
              </RadioGroup>
              {pickupData.isUseVehicleSize && (
                <>
                  <Divider orientation="vertical" color="gray200" />
                  <Select
                    className={styles['select']}
                    size="small"
                    placeholder="차량 크기 선택"
                    maxWidth="203px"
                    dropdownPortalTarget={document.body}
                    shouldCloseOnNotInView
                    options={filterOptions?.vehicleSize ?? []}
                    status={pickupDataState.vehicleSize.status}
                    validationMessage={pickupDataState.vehicleSize.message}
                    value={pickupData.vehicleSize ?? null}
                    disabled={disabled}
                    onChange={(
                      _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                      value: TypeSelectOption<string>
                    ) => onPickupDataChange('vehicleSize', { ...pickupData, vehicleSize: value })}
                    onClick={() => resetPickupDataState('vehicleSize')}
                  />
                </>
              )}
            </>
          }
          contentsClassName={styles['inputs-wrapper']}
        />
        <FormField
          label="파렛트 수"
          required
          tooltip={{
            description: RECEIVE_TOOLTIP_MESSAGE.PICKUP_PALLET_QUANTITY,
            maxWidth: '158px',
          }}
          contents={
            <div className={styles['textarea-wrapper']} onClick={() => resetPickupDataState('palletQuantity')}>
              <Textarea
                ref={(el) => {
                  if (el) errorFieldRefs.current[`${errorKeyPrefix}.palletQuantity`] = el;
                }}
                className={styles['textarea']}
                size="small"
                placeholder="정확한 수량과 규격 입력 (예: 1100X1100 3개)"
                maxWidth="389px"
                height="78px"
                disabled={disabled}
                count
                maxLength={85}
                status={pickupDataState.palletQuantity.status}
                validationMessage={pickupDataState.palletQuantity.message}
                value={pickupData.palletQuantity}
                onChange={(e) => {
                  onPickupDataChange('palletQuantity', { ...pickupData, palletQuantity: e.target.value });
                  resetPickupDataState('palletQuantity');
                }}
                onBlur={(e) =>
                  onPickupDataChange('palletQuantity', { ...pickupData, palletQuantity: e.target.value.trim() })
                }
              />
            </div>
          }
          contentsClassName={styles['inputs-wrapper']}
        />
        <FormField
          label="메모"
          contents={
            <Textarea
              className={styles['textarea']}
              size="small"
              placeholder="픽업서비스 관련 요청 또는 특이사항 입력"
              maxWidth="389px"
              height="78px"
              disabled={disabled}
              count
              maxLength={85}
              value={pickupData.memo}
              onChange={(e) => onPickupDataChange('memo', { ...pickupData, memo: e.target.value })}
              onBlur={(e) => onPickupDataChange('memo', { ...pickupData, memo: e.target.value.trim() })}
            />
          }
          contentsClassName={styles['inputs-wrapper']}
        />
        {pickupAddressModal.isOpen && (
          <ReceivePickupAddressModal
            mode={hasPickupAddress ? 'detail' : 'register'}
            data={addressData}
            open={pickupAddressModal.isOpen}
            onCancel={pickupAddressModal.handleCloseModal}
            onConfirm={pickupAddressModal.handleConfirmModal}
          />
        )}
      </div>
    </>
  );
};

export default ReceivePickupInfo;
